import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  useDisclosure,
  useToast,
  Text,
} from '@chakra-ui/react';
import {
  ConnectContactRequest,
  Contact,
  ContactAddToType,
  SearchQuery,
  contactsApi,
  defaultSearchQuery,
} from '@texas/api/endpoints/contactsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsColumns } from './Columns';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { ApiRequestType, useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ContactsModal } from '@texas/components/contacts/ContactsModal';
import { TexasHeader } from '@texas/components/shared/TexasHeader';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { EditContactModal } from './EditContactModal';
import { contactEvents } from './events';
import { VerifyDialogWithRequest } from '../shared/dialog/VerifyDialogWithRequest';

const defaultPageSize = 50;

interface ContactsTableProps {
  addToType: ContactAddToType;
  referenceId: number;
  referenceName: string;
  disconnectRequest: ApiRequestType<[request: ConnectContactRequest], Contact>;
  connectRequest: ApiRequestType<[request: ConnectContactRequest], Contact>;
}

export function ContactsTable(props: ContactsTableProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();

  const {
    data: contacts,
    refetch,
    error,
    loading,
  } = useApiResource(contactsApi.getAll);

  const [loadingMenuId, setLoadingMenuId] = useState<number | null>(null);

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    ...defaultSearchQuery,
    pageSize: defaultPageSize,
    customerId:
      props.addToType === ContactAddToType.Customer ? props.referenceId : null,
    supplierId:
      props.addToType === ContactAddToType.Supplier ? props.referenceId : null,
  });

  useEffect(
    () =>
      contactEvents.userConnected.sub((contactId) => {
        if (contacts?.items.some((contact) => contact.id === contactId)) {
          refetch(searchQuery);
        }
      }),
    [contacts, refetch, searchQuery],
  );

  const {
    onOpen: onAddOpen,
    onClose: onAddClose,
    isOpen: isAddOpen,
  } = useDisclosure();

  const {
    value: archiveValue,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
    isOpen: isArchiveOpen,
  } = useValueDisclosure<number>();

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const { request: disconnectRequest } = useApiRequest(props.disconnectRequest);
  const { request: connectRequest } = useApiRequest(props.connectRequest);
  const { request: archiveRequest, loading: archiveLoading } = useApiRequest(
    contactsApi.archive,
  );
  const { request: restoreRequest } = useApiRequest(contactsApi.restore);

  const onAdd = useCallback(
    async (data: ConnectContactRequest) =>
      await request(connectRequest, [data], () => {
        toast({
          title: t('general.added'),
          status: 'success',
          isClosable: true,
        });
        refetch(searchQuery);
      }),
    [connectRequest, refetch, searchQuery, t, toast],
  );

  const columns = useMemo(() => {
    const onRemove = async (data: ConnectContactRequest) => {
      setLoadingMenuId(data.id);
      await request(
        disconnectRequest,
        [data],
        () => {
          toast({
            title: t('general.successfullyRemoved'),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (connectError: ServerError) => {
          toast({
            title: t('general.removeFailed'),
            description: connectError.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
      setLoadingMenuId(null);
    };

    const onRestore = async (data: number) => {
      setLoadingMenuId(data);
      await request(
        restoreRequest,
        [data],
        () => {
          toast({
            title: t('general.restored'),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (connectError: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: connectError.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
      setLoadingMenuId(null);
    };

    return contactsColumns(
      t,
      props.referenceId,
      onRemove,
      onEditOpen,
      onArchiveOpen,
      onRestore,
      loadingMenuId,
    );
  }, [
    disconnectRequest,
    loadingMenuId,
    restoreRequest,
    onArchiveOpen,
    onEditOpen,
    props.referenceId,
    refetch,
    searchQuery,
    t,
    toast,
  ]);

  return (
    <BodyOverride>
      <ContactsModal
        onSelect={async (c) => {
          await onAdd({ id: c.id, referenceId: props.referenceId });
          onAddClose();
        }}
        onCreatedAndConnected={() => onAddClose()}
        mode="link"
        filterOn={null}
        addTo={props.addToType}
        referenceId={props.referenceId}
        onClose={() => {
          refetch(searchQuery);
          onAddClose();
        }}
        isOpen={isAddOpen}
      />
      {editValue && (
        <EditContactModal
          onUpdated={() => refetch(searchQuery)}
          onClose={onEditClose}
          isOpen={isEditOpen}
          contactId={editValue}
          customerId={
            props.addToType === ContactAddToType.Customer
              ? props.referenceId
              : undefined
          }
          supplierId={
            props.addToType === ContactAddToType.Supplier
              ? props.referenceId
              : undefined
          }
          referenceName={props.referenceName}
        />
      )}
      {archiveValue && (
        <VerifyDialogWithRequest
          headerTitle={t('contacts.archiveContactAndUser')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={archiveRequest}
          args={[archiveValue]}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onSuccessTitle={t('general.archived')}
          onPerformed={() => refetch(searchQuery)}
        >
          <Text>{t('contacts.archiveContactAndUserInfo')}</Text>
        </VerifyDialogWithRequest>
      )}
      <Card variant="dark">
        <CardBody>
          <Flex direction="column" gap={4}>
            <TexasHeader text={t('contacts.contacts')} />
            <Flex justify="space-between">
              <Box height="fit-content" mt="auto" w={400}>
                <SearchFilterInput
                  value={searchQuery.searchTerm}
                  placeholder={t('contacts.searcNameOrEmail')}
                  onChange={(s) =>
                    setSearchQuery({ ...searchQuery, searchTerm: s, page: 1 })
                  }
                />
              </Box>
              <Button variant="texas-solid" onClick={onAddOpen}>
                {t('contacts.addContact')}
              </Button>
            </Flex>
            <TexasSwitch
              checked={searchQuery.includeArchived}
              label={t('general.includeArchived')}
              onChange={(checked) =>
                setSearchQuery({
                  ...searchQuery,
                  includeArchived: checked,
                  page: 1,
                })
              }
            />
            <DataTableContainer
              w="100%"
              error={error}
              datatable={{
                data: contacts?.items ?? [],
                sorted: {
                  onSortedChange: ({ key, desc }) => {
                    setSearchQuery({
                      ...searchQuery,
                      sortBy: key,
                      sortDesc: desc,
                    });
                  },
                  key: searchQuery.sortBy,
                  desc: searchQuery.sortDesc,
                },
                isLoading: loading || archiveLoading,
                columns: columns,
                variant: 'configuration',
              }}
              pagination={{
                totalItems: contacts?.totalItems ?? 0,
                pageSize: defaultPageSize,
                currentPage: searchQuery.page,
                onPageChange: (page) =>
                  setSearchQuery({
                    ...searchQuery,
                    page,
                  }),
              }}
            />
          </Flex>
        </CardBody>
      </Card>
    </BodyOverride>
  );
}
